import {FactoryFn} from '../factory-function';
import {Image, Product as ClareProduct, Variation} from '@sportngin/se-commerce-sdk/models/clare';

export interface Product extends ClareProduct {
  only_sold_in_bundles?: boolean;
  bundledPrice?: number;
  price?: number;
  isBundle?: boolean;
  tax_code?: string;
}

export interface ListViewItem extends Product {
  images?: Image[];
}

export const getProductId = (product: Product | undefined): string | undefined =>
  product?.id !== undefined && product?.id !== null ? String(product.id) : undefined;

export const getProductName = (product: Product | undefined): string => product?.name ?? '';

export const getProductDescription = (product: Product | undefined): string | undefined =>
  product?.description ?? undefined;

export const getProductImages = (product: Product | undefined): Image[] | undefined =>
  product?.display_metadata?.images;

export const isProductActive = (product: Product | undefined): boolean => product?.active ?? false;

export const isBundleProduct = (product: Product | undefined): boolean =>
  ((product as (Product & {isBundle?: boolean}) | undefined)?.isBundle ?? false) === true;

/**
 * Compares Clare variation ids when API types differ (number vs string).
 */
export const variationIdsMatch = (a: unknown, b: unknown): boolean => {
  let matches = false;
  if (a !== undefined && a !== null && b !== undefined && b !== null) {
    matches = String(a) === String(b);
  }
  return matches;
};

/**
 * Finds a variation in the list by default variation id with normalized id matching.
 */
export const findItemVariationById = (
  variations: Variation[] | undefined,
  defaultVariationId: number | undefined
): Variation | undefined => {
  let found: Variation | undefined;
  if (variations !== undefined && defaultVariationId !== undefined) {
    found = variations.find(v => variationIdsMatch(v.id, defaultVariationId));
  }
  return found;
};

/**
 * After PATCH updates variation price, the API may still return a product with `variations: []` and
 * GET /finance/products/:id/variations may return []. Merge the variation id and cents we sent so
 * `getProductDetailDisplayPrice` and the edit modal reflect the saved price without a full reload.
 */
export function mergeClientPriceIntoItem(item: Product, variationId: unknown, priceCents: number): Product {
  let result = item;
  if (variationId !== undefined && variationId !== null) {
    const rows = [...(item.variations ?? [])];
    const idx = rows.findIndex(v => variationIdsMatch(v.id, variationId));
    let nextRows: Variation[];
    if (idx >= 0) {
      nextRows = rows.map((row, i) => (i === idx ? {...row, price_cents: priceCents} : row));
    } else {
      const idNum =
        typeof variationId === 'number'
          ? variationId
          : typeof variationId === 'string'
            ? Number(variationId)
            : Number.NaN;
      nextRows = Number.isNaN(idNum) ? rows : ([...rows, {id: idNum, price_cents: priceCents}] as Variation[]);
    }
    if (nextRows !== rows) {
      result = {...item, variations: nextRows};
    }
  }
  return result;
}

/**
 * Resolves display price in dollars from price_cents, price, or price_formatted.
 */
export const getItemVariationPriceInDollars = (variation: Variation | undefined): number | undefined => {
  let result: number | undefined;
  if (variation) {
    const centsRaw = variation.price_cents;
    if (centsRaw !== null && centsRaw !== undefined) {
      const cents = Number(centsRaw);
      if (!Number.isNaN(cents)) {
        result = cents / 100;
      }
    }
    if (result === undefined) {
      const direct = variation.price;
      if (direct !== null && direct !== undefined) {
        const n = typeof direct === 'string' ? Number(direct) : direct;
        if (!Number.isNaN(Number(n))) {
          result = Number(n);
        }
      }
    }
    if (result === undefined && typeof variation.price_formatted === 'string') {
      const sanitized = variation.price_formatted.replace(/[^0-9.-]/g, '');
      if (sanitized !== '') {
        const parsed = Number(sanitized);
        if (!Number.isNaN(parsed)) {
          result = parsed;
        }
      }
    }
  }
  return result;
};

/**
 * Resolves the default Clare variation from the list or embedded default_variation.
 */
export const resolveDefaultItemVariationForDisplay = (item: Product): Variation | undefined => {
  let variation: Variation | undefined;
  const defaultId = item.default_variation_id ?? item.default_variation?.id;
  const hasVariationRows = (item.variations?.length ?? 0) > 0;

  if (defaultId !== undefined && item.variations?.length) {
    variation = findItemVariationById(item.variations, defaultId);
  }
  if (variation === undefined && item.default_variation) {
    const embedded = item.default_variation;
    if (defaultId === undefined) {
      variation = embedded;
    } else if (!hasVariationRows) {
      // No variation rows: embedded default is authoritative (ids may omit or mismatch across controllers)
      variation = embedded;
    } else if (variationIdsMatch(embedded.id, defaultId)) {
      variation = embedded;
    }
  }
  // Stale default_variation_id (e.g. regenerated variants): id not in list — show first variation by position
  if (variation === undefined && hasVariationRows) {
    const sorted = [...(item.variations ?? [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    variation = sorted[0];
  }
  return variation;
};

/**
 * True when the item payload includes variation rows, embedded default variation, or explicit
 * item-level price fields. Used so list rows show $0.00 only when the API actually sent price
 * data (including free). `default_variation_id` alone is excluded: summary responses often omit
 * variations and prices, so a lone id must not force `price = 0`.
 */
export function hasProductPriceOrVariationContext(item: Product | undefined): boolean {
  if (!item) {
    return false;
  }
  return (
    (item.price_cents !== undefined && item.price_cents !== null) ||
    (item.price !== undefined && item.price !== null) ||
    (item.variations?.length ?? 0) > 0 ||
    (item.default_variation !== undefined && item.default_variation !== null)
  );
}

/**
 * Dollar amount for product detail UI: default variation price, then item-level price.
 */
export const getProductDetailDisplayPrice = (item: Product | undefined): number => {
  let price = 0;
  if (item?.price_cents) {
    price = item.price_cents / 100;
  } else if (item) {
    const variation = resolveDefaultItemVariationForDisplay(item);
    const fromVariation = getItemVariationPriceInDollars(variation);
    if (fromVariation !== undefined && !Number.isNaN(fromVariation)) {
      price = fromVariation;
    } else if (item.price_cents !== null && item.price_cents !== undefined) {
      const cents = Number(item.price_cents);
      if (!Number.isNaN(cents)) {
        price = cents / 100;
      }
    } else if (item.price !== null && item.price !== undefined) {
      const n = typeof item.price === 'string' ? Number(item.price) : item.price;
      if (!Number.isNaN(Number(n))) {
        price = Number(n);
      }
    }
  }
  return price;
};

export const clareProductToProduct: FactoryFn<Product, Product> = (item: Product): Product => {
  const active = item.default_variation?.active ?? item.active ?? false;
  const product: Product = {
    ...item,
    active,
    only_sold_in_bundles: item.only_sold_in_bundles ?? false,
  };

  if ((<ListViewItem>item).images) {
    product.display_metadata = {
      ...(product.display_metadata || {}),
      images: (<ListViewItem>item).images,
    };
  }

  // List rows omit `price` when there is no variation/price context so the table can show "—"
  // instead of $0.00 for products not yet priced. Detail still uses getProductDetailDisplayPrice.
  const displayPrice = getProductDetailDisplayPrice(item);
  if (hasProductPriceOrVariationContext(item) || displayPrice > 0) {
    product.price = displayPrice;
  }

  return product;
};

export const ocProductToClareProduct: FactoryFn<Product, Product> = (product: Product): Product => {
  const clareProduct: Product = {
    id: product.id,
    name: product.name ?? '',
    description: product.description,
    active: product.active ?? false,
    only_sold_in_bundles: product.only_sold_in_bundles ?? false,
    created_at: '',
  };

  if (product.uuid !== undefined) {
    clareProduct.uuid = String(product.uuid);
  }

  if (product.display_metadata) {
    clareProduct.display_metadata = product.display_metadata;
  }

  return clareProduct;
};
